import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtEur, fmtThousands } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { GitCompareArrows, Cpu, ShieldCheck, KeyRound, Bot, TrendingUp, Layers, Check, CreditCard, Bitcoin, Lock } from "lucide-react";
import { CryptoModal } from "./Pricing";
import perf from "../data/ea_performance.json";

const T = {
  it: {
    kicker: "Trading algoritmico", title: "Expert Advisor per MetaTrader 4",
    sub: "I sistemi quantitativi BundStrategy, compilati e protetti, che operano in totale autonomia sul tuo conto. Backtest 15-22 anni con costi reali, validazione out-of-sample, licenza legata al tuo conto MT4.",
    buy: "Acquista", owned: "Già acquistato", goto_dl: "Vai al download",
    hydra_tag: "Arbitraggio statistico multi-coppia",
    hydra_pitch: "Il cavallo di battaglia. Non prevede il mercato: incassa il riallineamento statistico di coppie gemelle. Ogni operazione è coperta — una gamba long e una short simultanee — quindi non ti interessa dove va il mercato, solo che lo spread rientri. E lo fa il 69% delle volte.",
    hydra_pts: [
      ["5 spread diversificati", "WTI/Brent (arbitraggio petrolifero, profittevole in ogni regime testato) + 4 spread valutari indipendenti."],
      ["Market-neutral", "Long e short simultanei di pari nozionale: l'esposizione direzionale netta è ~zero. Crolli e rally non sono il tuo problema."],
      ["~21 operazioni/anno, 6-8 giorni", "Frequenza da portafoglio, non da scommessa singola. Win rate storico 69%."],
      ["Breaker anti-crisi integrato", "Se uno spread perde 5R in 10 trade, l'EA lo sospende e lo monitora su carta finché non torna profittevole."],
    ],
    atlas_tag: "Trend-following su compressione di volatilità",
    atlas_pitch: "Il cacciatore di trend. Aspetta che Oro, Argento, S&P500 e Dow \u201csi comprimano\u201d (bassa volatilità = energia accumulata) ed entra sull'esplosione. Perdita massima storica per trade: −1,2R. Vincita massima: +13R. Un'asimmetria che paga anche vincendo meno di una volta su due.",
    atlas_pts: [
      ["Asimmetria estrema", "Stop stretto (3×ATR) e trailing che insegue il trend per mesi: 29 trade storici oltre +2R, worst case −1,2R."],
      ["Zero overfitting dimostrato", "Out-of-sample 2018-26 MIGLIORE del train (PF 2,93 vs 2,03). Dow e Argento aggiunti dopo la calibrazione: profittevoli."],
      ["Robusto ai costi", "Con costi maggiorati del 50% il Profit Factor resta 2,23."],
      ["Il complemento perfetto di HYDRA", "Guadagna proprio nei grandi trend/crisi dove l'arbitraggio soffre."],
    ],
    stats: { cagr: "CAGR storico", maxdd: "Max drawdown", pf: "Profit Factor", wr: "Win rate", nyr: "Trade/anno", risk: "Rischio per trade" },
    equity_title: "Equity line — €5.000 iniziali, capitalizzazione composta",
    equity_note_h: "Backtest 2012-2026, costi Axi Pro inclusi (spread + commissioni + swap), rischio 2% per trade.",
    equity_note_a: "Backtest 2004-2026, costi inclusi, rischio 1,5% per trade.",
    yearly_title: "Rendimenti annuali",
    bundle_kicker: "L'acquisto intelligente", bundle_title: "Bundle HYDRA + ATLAS: due logiche opposte, un solo drawdown",
    bundle_pitch: "HYDRA guadagna nei mercati normali, dove i prezzi oscillano e gli spread rientrano. ATLAS guadagna nei grandi trend e nelle crisi, dove tutto si muove nella stessa direzione. Farli girare insieme sullo stesso conto non somma solo i profitti — compensa i momenti deboli dell'uno con i momenti forti dell'altro:",
    bundle_stats: [
      ["+38,2%", "CAGR combinato storico (vs +30,1% di HYDRA da sola)"],
      ["−28,9%", "Max drawdown del portafoglio combinato: IDENTICO a quello di HYDRA da sola. ATLAS aggiunge rendimento, non rischio"],
      ["~31", "operazioni/anno complessive"],
      ["€149", "di risparmio rispetto all'acquisto separato"],
    ],
    bundle_chart: "Equity combinata (dal 2012, €5.000 iniziali)",
    protect_title: "Licenza e protezione",
    protect: [
      ["File compilato .ex4", "Ricevi solo l'eseguibile protetto: nessuno può leggere, copiare o modificare la logica del sistema."],
      ["Licenza legata al TUO conto MT4", "Dopo l'acquisto registri il numero del tuo conto: l'EA verifica la licenza sul nostro server a ogni avvio e ogni 24h. Su conti non registrati non opera."],
      ["Una licenza, un conto", "Il file è inutilizzabile se rivenduto o condiviso: sul conto di chiunque altro l'EA si blocca. Cambio conto? Lo sposti contattando l'assistenza."],
      ["Aggiornamenti inclusi", "Ogni futura revisione degli EA è scaricabile senza costi aggiuntivi dalla tua area riservata."],
    ],
    how_title: "Come funziona l'acquisto",
    how: ["Acquista con carta (Stripe) o crypto", "Registra il numero del tuo conto MT4 nella pagina Download", "Scarica il file .ex4 e installalo (guida passo-passo inclusa)", "Valida in demo 1-3 mesi, poi passa al reale"],
    disclaimer: "I risultati presentati derivano da backtest su dati storici con costi di transazione realistici. Le performance passate non garantiscono risultati futuri. Il trading in leva comporta un elevato rischio di perdita del capitale. Gli EA sono strumenti software: la responsabilità dell'operatività resta dell'utente. Non costituisce consulenza finanziaria.",
    login_first: "Accedi o registrati per acquistare",
    price_once: "una tantum · licenza a vita",
    vs_title: "Quale scegliere?",
    vs_rows: [
      ["Logica", "Mean reversion (lo spread rientra)", "Trend following (il movimento continua)"],
      ["Esposizione", "Market-neutral (long+short)", "Direzionale (long o short)"],
      ["Win rate", "69% — tanti piccoli guadagni", "44% — pochi grandi guadagni"],
      ["Frequenza", "~21 trade/anno, brevi (6-8 gg)", "~8,5 trade/anno, lunghi (~36 gg)"],
      ["Brilla quando", "Mercati normali e laterali", "Grandi trend e crisi"],
      ["CAGR storico", "+30,1% (rischio 2%)", "+7,0% (rischio 1,5%)"],
    ],
  },
  en: {
    kicker: "Algorithmic trading", title: "Expert Advisors for MetaTrader 4",
    sub: "BundStrategy's quantitative systems, compiled and protected, trading fully autonomously on your account. 15-22 year backtests with real costs, out-of-sample validation, license bound to your MT4 account.",
    buy: "Buy now", owned: "Already purchased", goto_dl: "Go to download",
    hydra_tag: "Multi-pair statistical arbitrage",
    hydra_pitch: "The workhorse. It doesn't predict the market: it collects the statistical re-alignment of twin pairs. Every trade is hedged — one long and one short leg simultaneously — so you don't care where the market goes, only that the spread reverts. Which it does 69% of the time.",
    hydra_pts: [
      ["5 diversified spreads", "WTI/Brent (oil arbitrage, profitable in every tested regime) + 4 independent currency spreads."],
      ["Market-neutral", "Simultaneous long and short legs of equal notional: net directional exposure ~zero. Crashes and rallies are not your problem."],
      ["~21 trades/year, 6-8 days", "Portfolio-grade frequency. Historical win rate 69%."],
      ["Built-in anti-crisis breaker", "If a spread loses 5R over 10 trades, the EA suspends it and paper-monitors it until it recovers."],
    ],
    atlas_tag: "Volatility-squeeze trend following",
    atlas_pitch: "The trend hunter. It waits for Gold, Silver, S&P500 and Dow to \u201ccompress\u201d (low volatility = stored energy) and enters on the breakout. Worst historical loss per trade: −1.2R. Best win: +13R. An asymmetry that pays even winning less than half the time.",
    atlas_pts: [
      ["Extreme asymmetry", "Tight stop (3×ATR) and a trailing stop that rides trends for months: 29 historical trades beyond +2R, worst case −1.2R."],
      ["Proven zero overfitting", "Out-of-sample 2018-26 BETTER than train (PF 2.93 vs 2.03). Dow and Silver added after calibration: profitable."],
      ["Cost-robust", "With costs increased by 50% the Profit Factor stays at 2.23."],
      ["HYDRA's perfect complement", "It earns precisely in the big trends/crises where arbitrage struggles."],
    ],
    stats: { cagr: "Historical CAGR", maxdd: "Max drawdown", pf: "Profit Factor", wr: "Win rate", nyr: "Trades/year", risk: "Risk per trade" },
    equity_title: "Equity line — €5,000 initial, compounded",
    equity_note_h: "Backtest 2012-2026, Axi Pro costs included (spread + commissions + swap), 2% risk per trade.",
    equity_note_a: "Backtest 2004-2026, costs included, 1.5% risk per trade.",
    yearly_title: "Yearly returns",
    bundle_kicker: "The smart buy", bundle_title: "HYDRA + ATLAS Bundle: two opposite logics, one drawdown",
    bundle_pitch: "HYDRA earns in normal markets, where prices oscillate and spreads revert. ATLAS earns in big trends and crises, where everything moves one way. Running both on the same account doesn't just add profits — it offsets each system's weak spells with the other's strong ones:",
    bundle_stats: [
      ["+38.2%", "Combined historical CAGR (vs +30.1% for HYDRA alone)"],
      ["−28.9%", "Combined portfolio max drawdown: IDENTICAL to HYDRA alone. ATLAS adds return, not risk"],
      ["~31", "total trades/year"],
      ["€149", "saved vs buying separately"],
    ],
    bundle_chart: "Combined equity (since 2012, €5,000 initial)",
    protect_title: "License & protection",
    protect: [
      ["Compiled .ex4 file", "You receive only the protected executable: nobody can read, copy or modify the system logic."],
      ["License bound to YOUR MT4 account", "After purchase you register your account number: the EA validates the license on our server at every start and every 24h. It won't trade on unregistered accounts."],
      ["One license, one account", "The file is useless if resold or shared: on anyone else's account the EA locks itself. Changing account? Contact support to move it."],
      ["Updates included", "Every future EA revision is downloadable at no extra cost from your members area."],
    ],
    how_title: "How purchasing works",
    how: ["Pay by card (Stripe) or crypto", "Register your MT4 account number on the Download page", "Download the .ex4 and install it (step-by-step guide included)", "Validate on demo for 1-3 months, then go live"],
    disclaimer: "Results shown derive from backtests on historical data with realistic transaction costs. Past performance does not guarantee future results. Leveraged trading carries a high risk of capital loss. EAs are software tools: trading responsibility remains with the user. This is not financial advice.",
    login_first: "Log in or register to purchase",
    price_once: "one-off · lifetime license",
    vs_title: "Which one to choose?",
    vs_rows: [
      ["Logic", "Mean reversion (spread reverts)", "Trend following (move continues)"],
      ["Exposure", "Market-neutral (long+short)", "Directional (long or short)"],
      ["Win rate", "69% — many small wins", "44% — few big wins"],
      ["Frequency", "~21 trades/yr, short (6-8 d)", "~8.5 trades/yr, long (~36 d)"],
      ["Shines when", "Normal, range-bound markets", "Big trends and crises"],
      ["Historical CAGR", "+30.1% (2% risk)", "+7.0% (1.5% risk)"],
    ],
  },
};

const EquityChart = ({ data, color, testid }) => (
  <div data-testid={testid} className="h-64 w-full">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
        <defs>
          <linearGradient id={`g-${testid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
        <XAxis dataKey="d" tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false}
          ticks={data.filter((_, i) => i % Math.ceil(data.length / 7) === 0).map((p) => p.d)} />
        <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false}
          tickFormatter={(v) => `€${fmtThousands(v)}`} width={72} />
        <Tooltip contentStyle={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.15)", fontSize: 12 }}
          labelStyle={{ color: "#94a3b8" }} formatter={(v) => [`€${fmtThousands(v)}`, "Equity"]} />
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#g-${testid})`} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const YearlyGrid = ({ yearly, testid }) => (
  <div data-testid={testid} className="flex flex-wrap gap-2">
    {Object.entries(yearly).map(([y, r]) => (
      <div key={y} className={`px-3 py-1.5 border text-xs font-mono ${r >= 0 ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/5" : "border-red-500/30 text-red-400 bg-red-500/5"}`}>
        {y} <span className="font-semibold">{r >= 0 ? "+" : ""}{r}%</span>
      </div>
    ))}
  </div>
);

const StatGrid = ({ s, labels, accent }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
    {[["cagr", `+${s.cagr}%`], ["maxdd", `−${s.maxdd}%`], ["pf", s.pf], ["wr", `${s.wr}%`], ["nyr", s.n_yr], ["risk", `${s.risk_pct}%`]].map(([k, v]) => (
      <div key={k} className="border border-white/10 bg-white/[0.02] p-3">
        <p className="text-[10px] uppercase tracking-widest text-slate-500">{labels[k]}</p>
        <p className={`font-mono font-bold text-lg mt-1 ${k === "maxdd" ? "text-red-400" : accent}`}>{v}</p>
      </div>
    ))}
  </div>
);

export default function EAStore() {
  const { lang } = useLang();
  const t = T[lang] || T.it;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [owned, setOwned] = useState([]);
  const [busy, setBusy] = useState("");
  const [cryptoPlan, setCryptoPlan] = useState(null);
  const it = lang === "it";

  useEffect(() => {
    api.get("/ea/products").then((r) => setPlans(r.data.plans));
    if (user) api.get("/ea/my").then((r) => setOwned(r.data.owned)).catch(() => {});
  }, [user]);

  const checkout = async (lookup_key) => {
    if (!user) { navigate("/login"); return; }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setBusy("");
    }
  };

  const planBy = (k) => plans.find((p) => p.lookup_key === k) || {};
  const isOwned = (k) => (planBy(k).ea || []).every((p) => owned.includes(p));

  const BuyButtons = ({ lookup_key, accent }) => isOwned(lookup_key) ? (
    <Link to="/downloads" data-testid={`ea-goto-download-${lookup_key}`}
      className="inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-400 px-5 py-2.5 text-sm hover:bg-emerald-500/10 transition-colors">
      <Check className="w-4 h-4" /> {t.owned} → {t.goto_dl}
    </Link>
  ) : (
    <div className="flex flex-wrap items-center gap-3">
      <button data-testid={`ea-buy-${lookup_key}`} onClick={() => checkout(lookup_key)} disabled={busy === lookup_key}
        className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-black transition-colors disabled:opacity-50 ${accent}`}>
        <CreditCard className="w-4 h-4" /> {t.buy} — {fmtEur(planBy(lookup_key).amount_eur, lang)}
      </button>
      <button data-testid={`ea-buy-crypto-${lookup_key}`}
        onClick={() => (user ? setCryptoPlan({ lookup_key, name: planBy(lookup_key).name, amount_eur: planBy(lookup_key).amount_eur }) : navigate("/login"))}
        className="inline-flex items-center gap-2 border border-white/15 text-slate-300 px-4 py-2.5 text-sm hover:bg-white/5 transition-colors">
        <Bitcoin className="w-4 h-4" /> Crypto
      </button>
      <span className="text-xs text-slate-500">{t.price_once}</span>
    </div>
  );

  return (
    <div data-testid="ea-store-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
      {/* HERO */}
      <div className="max-w-3xl">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400 flex items-center gap-2"><Bot className="w-4 h-4" /> {t.kicker}</p>
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-white mt-3 leading-tight">{t.title}</h1>
        <p className="text-slate-400 leading-relaxed mt-5">{t.sub}</p>
        {!user && <p className="text-xs text-amber-400/90 mt-3">{t.login_first}</p>}
      </div>

      {/* HYDRA */}
      <section data-testid="ea-section-hydra" className="mt-16 border border-cyan-500/25 bg-cyan-500/[0.03] p-6 sm:p-10">
        <div className="flex items-center gap-3">
          <GitCompareArrows className="w-8 h-8 text-cyan-400" />
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-2xl text-white">HYDRA StatArb</h2>
            <p className="text-xs font-mono uppercase tracking-widest text-cyan-400/80">{t.hydra_tag}</p>
          </div>
        </div>
        <p className="text-slate-300 leading-relaxed mt-5 max-w-3xl">{t.hydra_pitch}</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {t.hydra_pts.map(([h, p], i) => (
            <div key={i} className="flex gap-3">
              <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-1" />
              <div><p className="text-sm font-semibold text-white">{h}</p><p className="text-sm text-slate-400 leading-relaxed">{p}</p></div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <p className="text-sm font-semibold text-white mb-1">{t.equity_title}</p>
          <p className="text-xs text-slate-500 mb-4">{t.equity_note_h}</p>
          <EquityChart data={perf.hydra.equity} color="#22d3ee" testid="hydra-equity-chart" />
        </div>
        <div className="mt-6"><StatGrid s={perf.hydra.stats} labels={t.stats} accent="text-cyan-400" /></div>
        <div className="mt-6">
          <p className="text-sm font-semibold text-white mb-3">{t.yearly_title}</p>
          <YearlyGrid yearly={perf.hydra.yearly} testid="hydra-yearly" />
        </div>
        <div className="mt-8"><BuyButtons lookup_key="ea_hydra" accent="bg-cyan-400 hover:bg-cyan-300" /></div>
      </section>

      {/* ATLAS */}
      <section data-testid="ea-section-atlas" className="mt-10 border border-amber-500/25 bg-amber-500/[0.03] p-6 sm:p-10">
        <div className="flex items-center gap-3">
          <Cpu className="w-8 h-8 text-amber-400" />
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-2xl text-white">ATLAS Breakout</h2>
            <p className="text-xs font-mono uppercase tracking-widest text-amber-400/80">{t.atlas_tag}</p>
          </div>
        </div>
        <p className="text-slate-300 leading-relaxed mt-5 max-w-3xl">{t.atlas_pitch}</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {t.atlas_pts.map(([h, p], i) => (
            <div key={i} className="flex gap-3">
              <Check className="w-4 h-4 text-amber-400 shrink-0 mt-1" />
              <div><p className="text-sm font-semibold text-white">{h}</p><p className="text-sm text-slate-400 leading-relaxed">{p}</p></div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <p className="text-sm font-semibold text-white mb-1">{t.equity_title}</p>
          <p className="text-xs text-slate-500 mb-4">{t.equity_note_a}</p>
          <EquityChart data={perf.atlas.equity} color="#fbbf24" testid="atlas-equity-chart" />
        </div>
        <div className="mt-6"><StatGrid s={perf.atlas.stats} labels={t.stats} accent="text-amber-400" /></div>
        <div className="mt-6">
          <p className="text-sm font-semibold text-white mb-3">{t.yearly_title}</p>
          <YearlyGrid yearly={perf.atlas.yearly} testid="atlas-yearly" />
        </div>
        <div className="mt-8"><BuyButtons lookup_key="ea_atlas" accent="bg-amber-400 hover:bg-amber-300" /></div>
      </section>

      {/* CONFRONTO */}
      <section className="mt-14">
        <h2 className="font-heading font-bold text-lg md:text-lg text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /> {t.vs_title}</h2>
        <div data-testid="ea-comparison-table" className="mt-5 border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-500">
              <th className="p-3"></th><th className="p-3 text-cyan-400">HYDRA</th><th className="p-3 text-amber-400">ATLAS</th></tr></thead>
            <tbody>
              {t.vs_rows.map(([k, h, a], i) => (
                <tr key={i} className={`border-t border-white/5 ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                  <td className="p-3 text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">{k}</td>
                  <td className="p-3 text-slate-300">{h}</td><td className="p-3 text-slate-300">{a}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* BUNDLE */}
      <section data-testid="ea-section-bundle" className="mt-14 border border-emerald-500/30 bg-emerald-500/[0.04] p-6 sm:p-10">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-emerald-400 flex items-center gap-2"><Layers className="w-4 h-4" /> {t.bundle_kicker}</p>
        <h2 className="font-heading font-bold text-2xl md:text-2xl text-white mt-2">{t.bundle_title}</h2>
        <p className="text-slate-300 leading-relaxed mt-4 max-w-3xl">{t.bundle_pitch}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {t.bundle_stats.map(([v, l], i) => (
            <div key={i} className="border border-emerald-500/20 bg-black/30 p-4">
              <p className="font-mono font-bold text-2xl text-emerald-400">{v}</p>
              <p className="text-xs text-slate-400 leading-relaxed mt-1">{l}</p>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <p className="text-sm font-semibold text-white mb-4">{t.bundle_chart}</p>
          <EquityChart data={perf.combo.equity} color="#34d399" testid="combo-equity-chart" />
        </div>
        <div className="mt-6"><YearlyGrid yearly={perf.combo.yearly} testid="combo-yearly" /></div>
        <div className="mt-8"><BuyButtons lookup_key="ea_bundle" accent="bg-emerald-400 hover:bg-emerald-300" /></div>
      </section>

      {/* LICENZA */}
      <section className="mt-14">
        <h2 className="font-heading font-bold text-lg md:text-lg text-white flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400" /> {t.protect_title}</h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {t.protect.map(([h, p], i) => (
            <div key={i} className="border border-white/10 bg-white/[0.02] p-5 flex gap-3">
              {i === 0 ? <Lock className="w-5 h-5 text-emerald-400 shrink-0" /> : <KeyRound className="w-5 h-5 text-emerald-400 shrink-0" />}
              <div><p className="text-sm font-semibold text-white">{h}</p><p className="text-sm text-slate-400 leading-relaxed mt-1">{p}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* COME FUNZIONA */}
      <section className="mt-14">
        <h2 className="font-heading font-bold text-lg md:text-lg text-white">{t.how_title}</h2>
        <ol className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {t.how.map((s, i) => (
            <li key={i} className="border border-white/10 bg-white/[0.02] p-4">
              <span className="font-mono text-emerald-400 text-lg font-bold">{i + 1}</span>
              <p className="text-sm text-slate-300 leading-relaxed mt-2">{s}</p>
            </li>
          ))}
        </ol>
      </section>

      <p className="text-xs text-slate-600 leading-relaxed mt-14 border-t border-white/5 pt-6">{t.disclaimer}</p>

      {cryptoPlan && <CryptoModal plan={cryptoPlan} onClose={() => setCryptoPlan(null)} />}
    </div>
  );
}
